import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { plansApi } from '../api/plans.api';
import type {
  PlanPriceMigration,
  PlanPricingImpact,
  UpdatePlanPricingRequest,
} from '../types/plans.types';
import { planKeys } from './queryKeys';

export const usePricingImpact = (planId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: planKeys.pricingImpact(planId),
    queryFn: () => plansApi.getPricingImpact(planId),
    enabled: !!planId,
  });

  return {
    impact: data?.data as PlanPricingImpact | undefined,
    isLoading,
  };
};

export const useUpdatePlanPricing = (planId: string) => {
  const queryClient = useQueryClient();

  const updatePricing = useMutation({
    mutationFn: (data: UpdatePlanPricingRequest) => plansApi.updatePlanPricing(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
      queryClient.invalidateQueries({ queryKey: planKeys.detail(planId) });
      queryClient.invalidateQueries({ queryKey: planKeys.pricingImpact(planId) });
      queryClient.invalidateQueries({ queryKey: planKeys.priceMigrations(planId) });
    },
  });

  return {
    updatePricing: updatePricing.mutate,
    isUpdating: updatePricing.isPending,
  };
};

export const usePriceMigrations = (planId: string) => {
  const { data, isLoading } = useQuery({
    queryKey: planKeys.priceMigrations(planId),
    queryFn: () => plansApi.getPriceMigrations(planId),
    enabled: !!planId,
  });

  return {
    migrations: (data?.data ?? []) as PlanPriceMigration[],
    isLoading,
  };
};

export const useCancelPriceMigration = (planId: string) => {
  const queryClient = useQueryClient();

  const cancelMigration = useMutation({
    mutationFn: ({ migrationId, reason }: { migrationId: string; reason?: string }) =>
      plansApi.cancelPriceMigration(migrationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.priceMigrations(planId) });
    },
  });

  return {
    cancelMigration: cancelMigration.mutate,
    isCancelling: cancelMigration.isPending,
  };
};
